import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import type { SpeechLocale, SpeechMode, SpeechVoiceDiagnostic } from '../core/speech/speech.service';

@Component({
  selector: 'app-audio-settings-screen',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="card adult-card audio-settings-card">
      <header class="adult-heading">
        <p class="eyebrow">Area adulto · audio</p>
        <h1>Audio e TTS</h1>
        <p>Scegli quale motore deve leggere istruzioni e feedback. La scelta resta su questo dispositivo.</p>
      </header>

      <fieldset class="speech-mode-fieldset" [disabled]="busyLocale() !== null">
        <legend>Motore vocale</legend>
        <div class="speech-mode-options">
          <label class="speech-mode-option" [class.is-selected]="mode() === 'local'">
            <input
              type="radio"
              name="speech-mode"
              value="local"
              [checked]="mode() === 'local'"
              (change)="modeChange.emit('local')"
            />
            <span class="speech-mode-copy">
              <strong>Voce locale Learning Garden</strong>
              <span>Funziona offline dopo il download, ma può essere lenta sui dispositivi meno potenti.</span>
            </span>
          </label>

          <label class="speech-mode-option" [class.is-selected]="mode() === 'device'">
            <input
              type="radio"
              name="speech-mode"
              value="device"
              [checked]="mode() === 'device'"
              (change)="modeChange.emit('device')"
            />
            <span class="speech-mode-copy">
              <strong>Voce del dispositivo</strong>
              <span>Usa la sintesi vocale disponibile tramite Android/browser e non avvia il modello locale.</span>
            </span>
          </label>
        </div>
      </fieldset>

      @if (mode() === 'device') {
        <div class="method-note">
          <strong>Voce del dispositivo attiva.</strong>
          Qualità, lingue disponibili e comportamento offline dipendono dal browser e dal sistema operativo.
        </div>

        <section class="device-speech-card" aria-labelledby="device-speech-title">
          <h2 id="device-speech-title">Voce del dispositivo attiva</h2>
          <p>Puoi provare le lingue usate dall'app senza caricare Paola o Lessac.</p>
          <div class="actions-inline">
            <button class="secondary" type="button" [disabled]="busyLocale() !== null" (click)="testVoice.emit('it-IT')">
              Prova italiano
            </button>
            <button class="secondary" type="button" [disabled]="busyLocale() !== null" (click)="testVoice.emit('en-US')">
              Prova inglese
            </button>
          </div>
        </section>

        <p class="privacy-note">
          In questa modalità il testo viene consegnato all'API vocale del browser/sistema. Learning Garden non lo invia
          a un proprio servizio TTS; l'eventuale uso di rete del motore di sistema dipende dalla configurazione del dispositivo.
        </p>
      } @else {
        <div class="method-note audio-method-note">
          <strong>Voce locale attiva:</strong> durante un cambio lingua l'app aspetta il motore locale. Il TTS del
          dispositivo viene usato solo se la voce locale non può essere avviata o riprodotta.
        </div>

        <div class="voice-settings-list">
          @for (voice of diagnostics(); track voice.locale) {
            <article class="voice-settings-card">
              <div class="voice-settings-heading">
                <div>
                  <p class="eyebrow">{{ voice.locale }}</p>
                  <h2>{{ voice.name }}</h2>
                </div>
                <span class="status-badge" [class.status-error]="voice.state === 'failed'">
                  {{ voiceStateLabel(voice) }}
                </span>
              </div>

              <dl class="voice-diagnostics">
                <div><dt>Memoria</dt><dd>{{ voice.active ? 'Caricata adesso' : 'Non caricata' }}</dd></div>
                <div><dt>Disponibilità offline</dt><dd>{{ cacheLabel(voice) }}</dd></div>
                <div><dt>Ultimo percorso audio</dt><dd>{{ routeLabel(voice) }}</dd></div>
                <div><dt>Dimensione voice pack</dt><dd>{{ packSizeLabel(voice) }}</dd></div>
              </dl>

              @if (voice.state === 'loading' && voice.progress !== null) {
                <div class="voice-progress" aria-live="polite">
                  <progress max="1" [value]="voice.progress"></progress>
                  <span>{{ (voice.progress * 100).toFixed(0) }}%</span>
                </div>
              }

              <p class="voice-status-message">{{ voice.message }}</p>

              <div class="actions-inline voice-actions">
                <button class="primary" type="button" [disabled]="busyLocale() !== null" (click)="prepareVoice.emit(voice.locale)">
                  {{ voice.cacheState === 'validated' ? 'Prepara voce' : 'Scarica e verifica' }}
                </button>
                <button class="secondary" type="button" [disabled]="busyLocale() !== null" (click)="testVoice.emit(voice.locale)">Prova voce</button>
              </div>

              <details class="voice-technical-details">
                <summary>Diagnostica tecnica</summary>
                <dl class="voice-diagnostics technical-diagnostics">
                  <div><dt>Modello</dt><dd>{{ voice.model || 'non ancora rilevato' }}</dd></div>
                  <div><dt>Build</dt><dd>{{ voice.buildId || 'non ancora rilevata' }}</dd></div>
                  <div><dt>Chunk da cache</dt><dd>{{ voice.cachedParts ?? 'n/d' }}</dd></div>
                  <div><dt>Chunk da rete</dt><dd>{{ voice.networkParts ?? 'n/d' }}</dd></div>
                </dl>
                @if (voice.lastError) {
                  <p class="voice-error"><strong>Ultimo errore:</strong> {{ voice.lastError }}</p>
                }
                <div class="voice-reset-actions">
                  <p class="privacy-note">Elimina solo i file locali di questa voce e li scarica di nuovo. Profili e progressi non vengono toccati.</p>
                  <button class="quiet-button" type="button" [disabled]="busyLocale() !== null" (click)="forceDownloadVoice.emit(voice.locale)">Riscarica da zero</button>
                </div>
              </details>
            </article>
          }
        </div>

        <p class="privacy-note">
          Le voci e la sintesi locale non inviano il testo degli esercizi a un servizio TTS remoto. La diagnostica mostrata qui
          riguarda solo stato tecnico, cache e percorso audio del dispositivo.
        </p>
      }

      @if (status()) {
        <p class="settings-status" aria-live="polite">{{ status() }}</p>
      }

      <div class="actions split-actions">
        <button class="secondary" type="button" [disabled]="busyLocale() !== null" (click)="refresh.emit()">Rileggi stato</button>
        <button class="primary" type="button" [disabled]="busyLocale() !== null" (click)="back.emit()">Indietro</button>
      </div>
    </section>
  `,
  styleUrl: './audio-settings-screen.component.scss'
})
export class AudioSettingsScreenComponent {
  readonly diagnostics = input.required<readonly SpeechVoiceDiagnostic[]>();
  readonly mode = input.required<SpeechMode>();
  readonly busyLocale = input<SpeechLocale | null>(null);
  readonly status = input<string | null>(null);

  readonly modeChange = output<SpeechMode>();
  readonly prepareVoice = output<SpeechLocale>();
  readonly testVoice = output<SpeechLocale>();
  readonly forceDownloadVoice = output<SpeechLocale>();
  readonly refresh = output<void>();
  readonly back = output<void>();

  protected voiceStateLabel(voice: SpeechVoiceDiagnostic): string {
    switch (voice.state) {
      case 'ready': return 'Pronta';
      case 'loading': return 'Caricamento';
      case 'cached': return 'In cache';
      case 'failed': return 'Errore';
      default: return 'Da preparare';
    }
  }

  protected cacheLabel(voice: SpeechVoiceDiagnostic): string {
    switch (voice.cacheState) {
      case 'validated': return 'Verificata e disponibile offline';
      case 'present': return 'File presenti; verifica al caricamento';
      case 'missing': return 'Download offline incompleto o assente';
      default: return 'Stato cache non ancora noto';
    }
  }

  protected routeLabel(voice: SpeechVoiceDiagnostic): string {
    switch (voice.lastRoute) {
      case 'local': return 'Motore locale';
      case 'local-loading': return 'In attesa del motore locale';
      case 'device': return 'Voce del dispositivo';
      case 'browser-fallback': return 'Fallback del dispositivo';
      case 'unavailable': return 'Audio non disponibile';
      default: return 'Non ancora usata in questa sessione';
    }
  }

  protected packSizeLabel(voice: SpeechVoiceDiagnostic): string {
    if (!voice.packSizeBytes) {
      return 'non disponibile';
    }
    const mib = voice.packSizeBytes / (1024 * 1024);
    return `${mib.toFixed(mib >= 10 ? 0 : 1)} MB`;
  }
}